import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from './session';
import { Shell } from './Shell';
import { LoginPage } from '@/pages/LoginPage';
import { OnboardingPage } from '@/pages/OnboardingPage';

const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const AssetsPage = lazy(() => import('@/pages/AssetsPage'));
const AssetDetailPage = lazy(() => import('@/pages/AssetDetailPage'));
const RiskAppraisalPage = lazy(() => import('@/pages/RiskAppraisalPage'));
const FindingsPage = lazy(() => import('@/pages/FindingsPage'));
const ActionsPage = lazy(() => import('@/pages/ActionsPage'));
const QueuePage = lazy(() => import('@/pages/QueuePage'));
const ImportPage = lazy(() => import('@/pages/ImportPage'));
const InspecoesPage = lazy(() => import('@/pages/InspecoesPage'));
const InspecaoPage = lazy(() => import('@/pages/InspecaoPage'));
const PropostasPage = lazy(() => import('@/pages/PropostasPage'));
const EntradaPelaMaquina = lazy(() => import('@/pages/EntradaPelaMaquina'));
const ActionDetailPage = lazy(() => import('@/pages/ActionDetailPage'));
const JornadaPage = lazy(() => import('@/pages/JornadaPage'));
const DocumentosPage = lazy(() => import('@/pages/DocumentosPage'));
const PlanoDeAcoesDocumentoPage = lazy(() => import('@/pages/PlanoDeAcoesDocumentoPage'));
const DocumentoVersaoPage = lazy(() => import('@/pages/DocumentoVersaoPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (count, error) => {
        const kind = (error as { kind?: string })?.kind;
        if (kind === 'forbidden' || kind === 'unauthorized' || kind === 'not_found') return false;
        return count < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function Loading() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', padding: 48 }}>
      <span className="spinner spinner--lg" aria-label="Carregando" />
    </div>
  );
}

/**
 * Sem sessão, a URL precisa dizer `/login`.
 *
 * Antes o Gate trocava só o conteúdo, e a barra do navegador continuava em
 * `/dashboard` depois do logout. Não era falha de acesso — sem sessão a RLS
 * não devolve linha nenhuma —, mas mentia sobre onde a pessoa está, quebrava
 * o botão voltar e deixava um endereço sem sentido para compartilhar.
 *
 * A rota de origem é guardada em `state.de` para devolver a pessoa ao lugar
 * onde ela estava depois de entrar.
 */
function PortaoDeEntrada() {
  const location = useLocation();
  if (location.pathname === '/login') return <LoginPage />;
  return <Navigate to="/login" replace state={{ de: location.pathname + location.search }} />;
}

function Gate() {
  const { session, loading, memberships } = useSession();
  const location = useLocation();

  if (loading) return <Loading />;
  if (!session) return <PortaoDeEntrada />;
  if (memberships.length === 0) return <OnboardingPage />;

  // Já entrou: `/login` não é lugar de ficar. Volta para onde estava.
  if (location.pathname === '/login') {
    const de = (location.state as { de?: string } | null)?.de;
    return <Navigate to={de && de !== '/login' ? de : '/dashboard'} replace />;
  }

  return (
    <Shell>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/ativos" element={<AssetsPage />} />
          <Route path="/ativos/:id" element={<AssetDetailPage />} />
          <Route path="/ativos/:id/apreciacao" element={<RiskAppraisalPage />} />
          <Route path="/inspecoes" element={<InspecoesPage />} />
          <Route path="/inspecoes/:runId" element={<InspecaoPage />} />
          {/* a entrada pelo dossiê resolve qual inspeção abrir e redireciona */}
          <Route path="/ativos/:id/checklist" element={<EntradaPelaMaquina />} />
          <Route path="/propostas" element={<PropostasPage />} />
          <Route path="/nao-conformidades" element={<FindingsPage />} />
          <Route path="/acoes" element={<ActionsPage />} />
          <Route path="/acoes/:id" element={<ActionDetailPage />} />
          <Route path="/jornada" element={<JornadaPage />} />
          <Route path="/documentos" element={<DocumentosPage />} />
          <Route path="/documentos/plano-de-acoes" element={<PlanoDeAcoesDocumentoPage />} />
          {/*
            A rota do documento é por IDENTIFICADOR, nunca pelo número visível:
            o número é do documento e se repete em todas as revisões, então na
            rota ele apontaria para várias coisas ao mesmo tempo.
          */}
          <Route
            path="/documentos/:documentId/versoes/:versionId"
            element={<DocumentoVersaoPage />}
          />
          <Route path="/importar" element={<ImportPage />} />
          <Route path="/fila" element={<QueuePage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </Shell>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <BrowserRouter>
          <Gate />
        </BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  );
}
